import { FC, ReactNode, useEffect, useRef, useState } from "react";

import { Box, Stack, SxProps, Theme } from "@mui/material";

import { isIOS } from "@components/BottomPlayer/components/Player/helpers";

import { useIsDesktop } from "@/hooks/layout";
import { CloseButton } from "@/ui-kit";

import { CustomDialog, ScrollWrapper, StyledBackDrop } from "./components";

interface IGradientDialog {
  open: boolean;
  scrollResizeInvoke?: boolean;
  children: ReactNode;
  handleClose: () => void;
  scroll?: "body" | "paper";
  isClosable?: boolean;
  background?: string;
  btn?: JSX.Element;
  staticContent?: JSX.Element;
  closeBtnStyles?: SxProps;
  leftRightPadding?: string;
  topBottomPadding?: string;
  isNotScrollable?: boolean;
  sx?: SxProps<Theme>;
}

const GradientDialog: FC<IGradientDialog> = ({
  open,
  scrollResizeInvoke,
  children,
  handleClose,
  scroll,
  isClosable = true,
  background,
  btn,
  staticContent,
  closeBtnStyles,
  leftRightPadding,
  topBottomPadding,
  isNotScrollable,
  sx,
}) => {
  const isDesktop = useIsDesktop();

  const intervalRef = useRef(null);
  const thumbRef = useRef<HTMLElement>(null);
  const thumbHeight = useRef(0);
  const contentContainerRef = useRef<HTMLElement>(null);

  const [isAccessible, setIsAccessible] = useState(false);

  const paddingTop = topBottomPadding ? topBottomPadding : isDesktop ? "40px" : "24px";
  const paddingBottom = topBottomPadding ? topBottomPadding : isDesktop ? "40px" : "24px";
  const paddingLeft = leftRightPadding ? leftRightPadding : isDesktop ? "40px" : "24px";
  const paddingRight = leftRightPadding ? leftRightPadding : isDesktop ? "40px" : "24px";

  const updateThumbPosition = () => {
    const contentContainer = contentContainerRef.current;

    const contentScrollHeight = contentContainer.scrollHeight;
    const contentClientHeight = contentContainer.clientHeight;

    const thumbHeightPx = (contentClientHeight / contentScrollHeight) * contentClientHeight;

    thumbHeight.current = thumbHeightPx;

    thumbRef.current.style.height = `${thumbHeightPx}px`;
  };
  const handleScroll = () => {
    const contentContainer = contentContainerRef.current;

    const scrollableHeight = contentContainer.scrollHeight - contentContainer.clientHeight;

    if (scrollableHeight < 0) return;
    if (scrollableHeight === 0) {
      thumbRef.current.style.top = `0px`;
      return;
    }

    const scrollTopPx = contentContainer.scrollTop;

    const maxThumbTopPx = contentContainer.clientHeight - thumbHeight.current;

    const thumbTopPx = Math.min(Math.max((scrollTopPx / scrollableHeight) * maxThumbTopPx, 0), maxThumbTopPx);

    thumbRef.current.style.top = `${thumbTopPx}px`;
  };

  useEffect(() => {
    if (!open && contentContainerRef.current) {
      contentContainerRef.current.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateThumbPosition);
      clearInterval(intervalRef.current);
      return;
    }

    if (contentContainerRef.current && isAccessible) {
      contentContainerRef.current.addEventListener("scroll", handleScroll);
      contentContainerRef.current.style.height = "fit-content";
      window.addEventListener("resize", updateThumbPosition);
      updateThumbPosition();
      handleScroll();
      intervalRef.current = setInterval(() => {
        if (contentContainerRef.current) {
          updateThumbPosition();
        }
      }, 50);
    }
  }, [isAccessible, open, scrollResizeInvoke]);
  return (
    <CustomDialog
      open={open}
      BackdropComponent={StyledBackDrop}
      scroll={scroll}
      sx={sx}
      /* A non-closable dialog ignores Escape as well as the backdrop click —
         otherwise ESC would dismiss a modal that has no other way to close
         (the ✕ is hidden), e.g. the logged-out login gate, leaving nothing
         behind it. Explicit closers (the ✕, action buttons) call handleClose
         directly and are unaffected. */
      disableEscapeKeyDown={isClosable === false}
      onClose={(_, reason) =>
        reason !== "backdropClick" && isClosable !== false && handleClose()
      }
      transitionDuration={350}
    >
      <Stack
        sx={{
          background: background || "transparent",
          position: "relative",
        }}
        width="100%"
        height="100%"
      >
        {isClosable && (
          <Box position="absolute" sx={{ top: "16px", right: "16px", zIndex: 10, ...closeBtnStyles }}>
            <CloseButton onClick={handleClose} />
          </Box>
        )}
        {isIOS() && !isNotScrollable ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingTop,
              paddingBottom,
            }}
          >
            <Box
              sx={{
                display: "flex",
              }}
            >
              <Stack gap={isDesktop ? "24px" : "16px"}>
                <Box
                  sx={{
                    paddingLeft,
                    paddingRight,
                  }}
                >
                  {staticContent}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <ScrollWrapper
                    className="scroll-wrapper"
                    ref={(ref: HTMLDivElement | null) => {
                      setIsAccessible(!!ref);
                      contentContainerRef.current = ref;
                    }}
                    sx={{
                      paddingLeft,
                      paddingRight,

                      maxHeight: `calc(100dvh - 64px - 40px - ${btn ? "76px" : "0px"})`,

                      "&&::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {children}
                  </ScrollWrapper>
                  <Box width={"4px"} sx={{ background: "#5c412a" }}>
                    <Box ref={thumbRef} sx={{ position: "relative", background: "#ac8d75", width: "100%" }}></Box>
                  </Box>
                </Box>
              </Stack>
            </Box>
            {btn && (
              <Box className="dialog-button" padding={isDesktop ? "0 40px" : "0 24px"}>
                {btn}
              </Box>
            )}
          </Box>
        ) : (
          <Stack
            className="scroll-wrapper"
            gap={isDesktop ? "24px" : "16px"}
            sx={{
              paddingTop,
              paddingBottom,
              maxHeight: "calc(100dvh - 64px - 40px)",
            }}
          >
            {staticContent && (
              <Box
                sx={{
                  paddingLeft,
                  paddingRight,
                }}
              >
                {staticContent}
              </Box>
            )}

            <ScrollWrapper
              sx={{
                paddingLeft,
                paddingRight,
                "&&": {
                  overflow: isNotScrollable ? "hidden" : "auto",
                },
              }}
            >
              {children}
            </ScrollWrapper>
            {btn && (
              <Box className="dialog-button" padding={isDesktop ? "10px 40px" : "10px 24px"}>
                {btn}
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </CustomDialog>
  );
};

export default GradientDialog;
